<script lang="ts">
/** One row of the Columns section. `locked` columns show "always" and can't be unticked. */
export interface GaiaColumnOption {
  key: string;
  label: string;
  visible: boolean;
  locked?: boolean;
}
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaSearchInput from '../base/inputs/GaiaSearchInput.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral, gaiaShadow } from '../themes/tokens';
import GaiaSwitch from '../base/GaiaSwitch.vue';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';

const props = defineProps({
  columns: {
    type: Array as PropType<GaiaColumnOption[]>,
    required: true,
  },
  /**
   * The "Exclude old revisions" switch above the list. Omit `toggleLabel` and
   * the whole row (and its divider) disappears — Matter has no revisions.
   */
  toggleLabel: {
    type: String,
    default: undefined,
  },
  toggleValue: {
    type: Boolean,
    default: false,
  },
  /** Tooltip on the ⓘ beside the switch. */
  toggleHint: {
    type: String,
    default: undefined,
  },
  /** The merge-mode entry. Omit to hide it — Matter can't be merged. */
  actionLabel: {
    type: String,
    default: undefined,
  },
  /** The action is on (merge mode running) — reads ink and semibold. */
  actionActive: {
    type: Boolean,
    default: false,
  },
  /**
   * Mounts a search field above the list. The filtering itself is the caller's:
   * it owns the column list, and a menu that hid rows the caller still counted
   * would disagree with it about what "all" means.
   */
  searchable: {
    type: Boolean,
    default: false,
  },
  search: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: 'Search columns…',
  },
  /**
   * Mounts the tri-state row that ticks or unticks everything in `columns` —
   * which, with a search running, is the matches rather than the whole set. The
   * caller supplies the wording because only it knows which of the two the row
   * is currently offering.
   */
  selectAllLabel: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  toggle: [key: string];
  'update:toggleValue': [on: boolean];
  'update:search': [value: string];
  /** Tick (true) or untick (false) every column currently listed. */
  'toggle-all': [visible: boolean];
  action: [];
}>();

/** Locked columns are always on and can't be ticked, so they don't count. */
const selectable = computed(() => props.columns.filter(column => !column.locked));
const allVisible = computed(
  () => selectable.value.length > 0 && selectable.value.every(column => column.visible)
);
const someVisible = computed(
  () => !allVisible.value && selectable.value.some(column => column.visible)
);

const rowSize = gaiaFontSize.dense;
const text = gaiaNeutral.text;
const ink = gaiaNeutral.ink;
const hover = gaiaNeutral.surfaceHover;
const divider = gaiaNeutral.borderSubtle;
const faint = gaiaNeutral.textFaint;
const hintBorder = gaiaNeutral.borderDashedSoft;
const surface = gaiaNeutral.surface;
const menuShadow = gaiaShadow.menu;
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- Behind the toolbar's ⋯: the revision switch, merge mode, then the column
       checklist. Host it in a GaiaPopover — this is only the menu body. -->
  <div class="gaia-columns-menu">
    <template v-if="props.toggleLabel">
      <button
        type="button"
        class="row switch-row"
        @click="emit('update:toggleValue', !props.toggleValue)"
      >
        <gaia-switch
          :value="props.toggleValue"
          size="small"
          tabindex="-1"
          style="pointer-events: none"
        />
        {{ props.toggleLabel }}
        <span v-if="props.toggleHint" class="hint" :title="props.toggleHint">i</span>
      </button>
      <div class="divider" />
    </template>

    <template v-if="props.actionLabel">
      <button
        type="button"
        class="row"
        :class="{ active: props.actionActive }"
        @click="emit('action')"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="flex: none"
        >
          <path d="M3 2.5v3.2c0 1.2.9 2.1 2.1 2.1h5.6M3 13.5v-3.2c0-1.2.9-2.1 2.1-2.1h5.6" />
          <path d="M9.4 5.6 11.9 8 9.4 10.4" />
        </svg>
        {{ props.actionLabel }}
      </button>
      <div class="divider" />
    </template>

    <gaia-text variant="label" size="nano" tone="faint" :style="{ padding: '5px 10px 6px' }">
      Columns
    </gaia-text>

    <gaia-search-input
      v-if="props.searchable"
      :value="props.search"
      variant="panel"
      :placeholder="props.searchPlaceholder"
      :style="{ margin: '0 4px 6px' }"
      @update:value="(value: string) => emit('update:search', value)"
    />

    <button
      v-if="props.selectAllLabel && selectable.length > 0"
      type="button"
      class="row option"
      @click="emit('toggle-all', !allVisible)"
    >
      <gaia-checkbox
        :checked="allVisible"
        :indeterminate="someVisible"
        size="small"
        tabindex="-1"
        style="pointer-events: none"
      />
      <span class="label">{{ props.selectAllLabel }}</span>
    </button>

    <gaia-text
      v-if="props.columns.length === 0"
      size="dense"
      tone="faint"
      :style="{ padding: '10px' }"
    >
      No columns match.
    </gaia-text>

    <div class="options">
      <button
        v-for="column in props.columns"
        :key="column.key"
        type="button"
        class="row option"
        :class="{ locked: column.locked }"
        @click="!column.locked && emit('toggle', column.key)"
      >
        <gaia-checkbox
          :checked="column.visible"
          :disabled="column.locked"
          size="small"
          tabindex="-1"
          style="pointer-events: none"
        />
        <span class="label">{{ column.label }}</span>
        <gaia-text v-if="column.locked" size="nano" tone="muted" :style="{ marginLeft: 'auto' }">
          always
        </gaia-text>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* GaiaPopover is `raw`, so the menu supplies its own surface. */
.gaia-columns-menu {
  width: 248px;
  display: flex;
  flex-direction: column;
  padding: 6px;
  border-radius: 10px;
  background: v-bind(surface);
  box-shadow: v-bind(menuShadow);
}

/* Every entry is a full-width <button> with a :hover, so its type and colours
   come through v-bind rather than a GaiaText prop. */
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: v-bind(rowSize);
  color: v-bind(text);
  text-align: left;
  cursor: pointer;
}

.row:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}

.row.locked {
  color: v-bind(faint);
  cursor: default;
}

.row.active {
  color: v-bind(ink);
  font-weight: v-bind(semibold);
}

.switch-row {
  height: 34px;
}

.label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.divider {
  height: 1px;
  margin: 5px 4px;
  background: v-bind(divider);
}

.options {
  max-height: 264px;
  overflow-y: auto;
}

/* A 15px glyph disc — the "i" is sized to the circle, not to the type scale. */
.hint {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  flex: none;
  margin-left: auto;
  border: 1px solid v-bind(hintBorder);
  border-radius: 50%;
  color: v-bind(faint);
  font-size: 9.5px;
  font-weight: v-bind(bold);
}
</style>
